import React from 'react'
import CardWrapper from '../CardWrapper'

// Helper function to convert video URLs to embeddable format
const getEmbeddableVideoUrl = (url) => {
  if (!url) return null;

  // YouTube URL conversion
  const youtubeRegex = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/;
  const youtubeMatch = url.match(youtubeRegex);
  if (youtubeMatch) {
    // Always ensure autoplay is disabled regardless of original URL parameters
    return `https://www.youtube.com/embed/${youtubeMatch[1]}?mute=1&enablejsapi=1&loop=1&playlist=${youtubeMatch[1]}&autoplay=0&controls=1`;
  }

  // Vimeo URL conversion - handles both vimeo.com and player.vimeo.com URLs
  const vimeoRegex = /(?:player\.vimeo\.com\/video\/|vimeo\.com\/)([0-9]+)/;
  const vimeoMatch = url.match(vimeoRegex);
  if (vimeoMatch) {
    // Extract video ID
    const videoId = vimeoMatch[1];

    // Parse existing URL to preserve some parameters but override autoplay
    const urlObj = new URL(url.includes('player.vimeo.com') ? url : `https://player.vimeo.com/video/${videoId}`);

    // Set our required parameters, overriding any existing ones
    urlObj.searchParams.set('autoplay', '0');
    urlObj.searchParams.set('controls', '1');
    urlObj.searchParams.set('title', '0');
    urlObj.searchParams.set('byline', '0');
    urlObj.searchParams.set('portrait', '0');
    urlObj.searchParams.set('muted', '0');

    // Keep existing parameters like 'h' (hash) if they exist
    return urlObj.toString();
  }

  // Loom URL conversion
  const loomRegex = /(?:loom\.com\/share\/)([^/?]+)/;
  const loomMatch = url.match(loomRegex);
  if (loomMatch) {
    return `https://www.loom.com/embed/${loomMatch[1]}?autoplay=0`;
  }

  // Jam URL conversion
  const jamRegex = /(?:jam\.dev\/c\/)([^/?]+)/;
  const jamMatch = url.match(jamRegex);
  if (jamMatch) {
    return `https://jam.dev/embed/${jamMatch[1]}?autoplay=0`;
  }

  // TikTok URL conversion
  const tiktokRegex = /(?:tiktok\.com\/@[^/]+\/video\/|vm\.tiktok\.com\/)([0-9]+)/;
  const tiktokMatch = url.match(tiktokRegex);
  if (tiktokMatch) {
    // TikTok embed doesn't support autoplay due to platform restrictions
    return `https://www.tiktok.com/embed/v2/${tiktokMatch[1]}`;
  }

  // Return original URL if not a supported platform
  return url;
};

const TabVideo = ({ video, text, description, poster, config, ...props }) => {
  const embeddableUrl = getEmbeddableVideoUrl(video);
  const isEmbeddableVideo = embeddableUrl && embeddableUrl !== video && (
    embeddableUrl.includes('youtube.com') ||
    embeddableUrl.includes('vimeo.com') ||
    embeddableUrl.includes('loom.com') ||
    embeddableUrl.includes('jam.dev') ||
    embeddableUrl.includes('tiktok.com')
  );

  const renderVideoContent = () => {
    if (isEmbeddableVideo) {
      return (
        <iframe
          src={embeddableUrl}
          title={text || 'Video'}
          className="h-[200px] w-full self-stretch rounded-v3-lg border-0"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
        />
      );
    }

    return (
      <video
        src={video}
        poster={poster}
        controls
        className="h-[200px] w-full self-stretch rounded-v3-lg object-cover bg-gray-300"
      >
        Your browser does not support the video tag.
      </video>
    );
  };

  if (text || description) {
    return (
      <CardWrapper text={text} description={description} config={config} hasLinks={true} {...props}>
        <div className='flex flex-col items-start self-stretch gap-[10px] pt-v3-xxs px-v3-xxs pb-0'>
          {renderVideoContent()}
        </div>
      </CardWrapper>
    )
  }

  const standaloneVideoContent = () => {
    if (isEmbeddableVideo) {
      return (
        <iframe
          src={embeddableUrl}
          title="Video"
          className="h-[200px] w-full rounded-v3-lg border-0"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
          {...props}
        />
      );
    }

    return (
      <video
        src={video}
        poster={poster}
        controls
        className="h-[200px] w-full rounded-v3-lg object-cover bg-gray-300"
        {...props}
      >
        Your browser does not support the video tag.
      </video>
    );
  };

  return standaloneVideoContent();
}

export default TabVideo
